import { useEffect, useState, useCallback } from "react";
import axios, { API } from "@/lib/api";
import { formatApiError } from "@/lib/apiError";
import { toast } from "sonner";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Sparkles, Loader2, Save, RefreshCw, Plus, X, Coins, Gem, Hexagon, Wrench,
} from "lucide-react";
import {
  KARATS, GOLD_COLORS, EMPTY_MATERIALS, normalize, serialize, freshUid,
} from "@/lib/materialsUtils";

const inputCls =
  "w-full bg-[#141414] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition";
const labelCls = "block text-[10px] uppercase tracking-[0.22em] text-white/45 mb-1";
const sectionTitleCls =
  "flex items-center gap-2 text-sm font-display text-[#D4AF37] uppercase tracking-[0.18em]";

function Section({ icon: Icon, title, children }) {
  return (
    <div className="rounded-xl border border-white/10 bg-[#0E0E0E]/60 p-4 space-y-3">
      <div className={sectionTitleCls}>
        <Icon className="w-3.5 h-3.5" />
        {title}
      </div>
      {children}
    </div>
  );
}

export default function MaterialsDrawer({ open, onOpenChange, render, onSaved }) {
  const renderId = render?.id;
  const [materials, setMaterials] = useState(EMPTY_MATERIALS);
  const [source, setSource] = useState(null); // 'ai' | 'manual' | null
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [estimating, setEstimating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const update = useCallback((patch) => {
    setMaterials((m) => ({ ...m, ...patch }));
    setDirty(true);
  }, []);
  const updateField = (group, field, value) => {
    setMaterials((m) => ({ ...m, [group]: { ...m[group], [field]: value } }));
    setDirty(true);
  };
  const updateListItem = (group, idx, field, value) => {
    setMaterials((m) => ({
      ...m,
      [group]: m[group].map((it, i) => (i === idx ? { ...it, [field]: value } : it)),
    }));
    setDirty(true);
  };
  const addListItem = (group, item) => {
    const withUid = { _uid: freshUid(group), ...item };
    setMaterials((m) => ({ ...m, [group]: [...m[group], withUid] }));
    setDirty(true);
  };
  const removeListItem = (group, idx) => {
    setMaterials((m) => ({ ...m, [group]: m[group].filter((_, i) => i !== idx) }));
    setDirty(true);
  };

  // Load saved materials when drawer opens for a render
  useEffect(() => {
    if (!open || !renderId) return;
    let alive = true;
    setLoading(true);
    axios
      .get(`${API}/studio/materials/${renderId}`)
      .then((res) => {
        if (!alive) return;
        setMaterials(normalize(res.data.materials));
        setSource(res.data.source || null);
        setUpdatedAt(res.data.updated_at || null);
        setDirty(false);
      })
      .catch((err) => {
        if (!alive) return;
        if (err?.response?.status !== 404) {
          toast.error("Could not load materials.");
        }
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [open, renderId]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setMaterials(EMPTY_MATERIALS);
      setSource(null);
      setUpdatedAt(null);
      setDirty(false);
    }
  }, [open]);

  const estimate = async () => {
    if (!renderId) return;
    setEstimating(true);
    try {
      const res = await axios.post(`${API}/studio/materials/${renderId}/estimate`, {}, {
        timeout: 120000,
      });
      setMaterials(normalize(res.data.materials));
      setSource("ai");
      setUpdatedAt(res.data.updated_at || null);
      setDirty(false);
      onSaved?.(renderId, res.data.materials, "ai");
      toast.success("AI estimate ready.");
    } catch (err) {
      toast.error(formatApiError(err, "Estimation failed."));
    } finally {
      setEstimating(false);
    }
  };

  const save = async () => {
    if (!renderId) return;
    setSaving(true);
    try {
      const res = await axios.put(`${API}/studio/materials/${renderId}`, serialize(materials));
      setMaterials(normalize(res.data.materials));
      setSource("manual");
      setUpdatedAt(res.data.updated_at || null);
      setDirty(false);
      onSaved?.(renderId, res.data.materials, "manual");
      toast.success("Materials saved.");
    } catch (err) {
      toast.error(formatApiError(err, "Save failed."));
    } finally {
      setSaving(false);
    }
  };

  const hasAny =
    materials.gold.weight_grams !== "" ||
    materials.diamonds.total_carat !== "" ||
    materials.gemstones.length > 0 ||
    materials.findings.length > 0 ||
    (materials.summary && materials.summary.trim());

  const priceLine = (() => {
    const { low, high, currency } = materials.price_range || {};
    if (low === "" && high === "") return null;
    const fmt = (n) =>
      n === "" || n === null ? "—" : `${currency === "INR" ? "₹" : currency} ${Number(n).toLocaleString("en-IN")}`;
    return `${fmt(low)} – ${fmt(high)}`;
  })();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        data-testid="materials-drawer"
        className="bg-[#0B0B0B] border-l border-white/10 text-white w-full sm:max-w-xl p-0 flex flex-col"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-white/10">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">
            <Sparkles className="w-3 h-3" /> Materials · BOM
          </div>
          <SheetTitle className="font-display text-2xl tracking-tight text-white">
            Bill of Materials
          </SheetTitle>
          <SheetDescription className="text-white/55 text-xs">
            AI-estimated weights, stones &amp; rough INR price range — every field is editable and
            saved per render.
          </SheetDescription>
          {(source || updatedAt) && (
            <div className="mt-1 text-[10px] text-white/40 flex items-center gap-2">
              {source === "ai" && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[#D4AF37]/40 text-[#D4AF37]">
                  <Sparkles className="w-2.5 h-2.5" /> AI estimate
                </span>
              )}
              {source === "manual" && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-white/15 text-white/65">
                  Edited
                </span>
              )}
              {updatedAt && <span>· {new Date(updatedAt).toLocaleString()}</span>}
            </div>
          )}
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {loading ? (
            <div className="flex items-center gap-2 text-white/55 text-sm py-10 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading materials…
            </div>
          ) : (
            <>
              {!hasAny && !estimating && (
                <div className="rounded-xl border border-dashed border-white/10 bg-[#141414] p-5 text-center">
                  <p className="text-sm text-white/65 mb-1">No materials yet</p>
                  <p className="text-[11px] text-white/40">
                    Let Gemini analyse this render and generate a starting BOM. Every value is
                    editable.
                  </p>
                </div>
              )}

              {/* Gold */}
              <Section icon={Coins} title="Gold">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Weight (g)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      data-testid="mat-gold-weight"
                      value={materials.gold.weight_grams}
                      onChange={(e) => updateField("gold", "weight_grams", e.target.value)}
                      placeholder="e.g. 22.5"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Karat</label>
                    <select
                      data-testid="mat-gold-karat"
                      value={materials.gold.karat || ""}
                      onChange={(e) => updateField("gold", "karat", e.target.value)}
                      className={inputCls}
                    >
                      {KARATS.map((k) => (
                        <option key={k} value={k}>
                          {k}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Color</label>
                    <select
                      data-testid="mat-gold-color"
                      value={materials.gold.color || ""}
                      onChange={(e) => updateField("gold", "color", e.target.value)}
                      className={inputCls}
                    >
                      {GOLD_COLORS.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Notes</label>
                    <input
                      type="text"
                      data-testid="mat-gold-notes"
                      value={materials.gold.notes}
                      onChange={(e) => updateField("gold", "notes", e.target.value)}
                      placeholder="Hallmark, finish…"
                      className={inputCls}
                    />
                  </div>
                </div>
              </Section>

              {/* Diamonds */}
              <Section icon={Gem} title="Diamonds">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Total carat</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      data-testid="mat-diamond-carat"
                      value={materials.diamonds.total_carat}
                      onChange={(e) => updateField("diamonds", "total_carat", e.target.value)}
                      placeholder="e.g. 1.85"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Count</label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      data-testid="mat-diamond-count"
                      value={materials.diamonds.count}
                      onChange={(e) => updateField("diamonds", "count", e.target.value)}
                      placeholder="e.g. 64"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Cut</label>
                    <input
                      type="text"
                      data-testid="mat-diamond-cut"
                      value={materials.diamonds.cut}
                      onChange={(e) => updateField("diamonds", "cut", e.target.value)}
                      placeholder="round / uncut polki / rose-cut…"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Quality</label>
                    <input
                      type="text"
                      data-testid="mat-diamond-quality"
                      value={materials.diamonds.quality}
                      onChange={(e) => updateField("diamonds", "quality", e.target.value)}
                      placeholder="GH / VS-SI…"
                      className={inputCls}
                    />
                  </div>
                </div>
                {materials.diamonds.notes !== undefined && (
                  <div>
                    <label className={labelCls}>Notes</label>
                    <input
                      type="text"
                      value={materials.diamonds.notes}
                      onChange={(e) => updateField("diamonds", "notes", e.target.value)}
                      placeholder="Setting style, certificate…"
                      className={inputCls}
                    />
                  </div>
                )}
              </Section>

              {/* Gemstones */}
              <Section icon={Hexagon} title="Gemstones">
                {materials.gemstones.length === 0 ? (
                  <p className="text-[11px] text-white/40">No gemstones added.</p>
                ) : (
                  <div className="space-y-2">
                    {materials.gemstones.map((g, i) => (
                      <div
                        key={g._uid || `gem-${i}`}
                        data-testid={`mat-gem-${i}`}
                        className="rounded-lg border border-white/10 bg-[#141414] p-3 space-y-2"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <input
                            type="text"
                            value={g.name}
                            onChange={(e) => updateListItem("gemstones", i, "name", e.target.value)}
                            placeholder="e.g. Emerald, Ruby, Pearl, Kundan"
                            className={inputCls + " flex-1"}
                          />
                          <button
                            onClick={() => removeListItem("gemstones", i)}
                            className="text-white/40 hover:text-red-400 p-1"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <input
                            type="text"
                            value={g.cut || ""}
                            onChange={(e) => updateListItem("gemstones", i, "cut", e.target.value)}
                            placeholder="cut"
                            className={inputCls}
                          />
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={g.count}
                            onChange={(e) => updateListItem("gemstones", i, "count", e.target.value)}
                            placeholder="count"
                            className={inputCls}
                          />
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={g.total_carat}
                            onChange={(e) =>
                              updateListItem("gemstones", i, "total_carat", e.target.value)
                            }
                            placeholder="carat"
                            className={inputCls}
                          />
                        </div>
                        <input
                          type="text"
                          value={g.notes || ""}
                          onChange={(e) => updateListItem("gemstones", i, "notes", e.target.value)}
                          placeholder="Notes (origin, treatment…)"
                          className={inputCls}
                        />
                      </div>
                    ))}
                  </div>
                )}
                <button
                  onClick={() =>
                    addListItem("gemstones", {
                      name: "",
                      cut: "",
                      count: "",
                      total_carat: "",
                      notes: "",
                    })
                  }
                  data-testid="mat-add-gemstone"
                  className="inline-flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-full border border-white/15 text-white/70 hover:border-[#D4AF37]/45 hover:text-[#D4AF37] transition"
                >
                  <Plus className="w-3 h-3" /> Add gemstone
                </button>
              </Section>

              {/* Findings */}
              <Section icon={Wrench} title="Findings &amp; Components">
                {materials.findings.length === 0 ? (
                  <p className="text-[11px] text-white/40">No findings added.</p>
                ) : (
                  <div className="space-y-2">
                    {materials.findings.map((f, i) => (
                      <div
                        key={f._uid || `find-${i}`}
                        data-testid={`mat-find-${i}`}
                        className="rounded-lg border border-white/10 bg-[#141414] p-3 space-y-2"
                      >
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={f.name}
                            onChange={(e) => updateListItem("findings", i, "name", e.target.value)}
                            placeholder="e.g. Screw-back, S-hook, Jump ring"
                            className={inputCls + " flex-1"}
                          />
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={f.count}
                            onChange={(e) => updateListItem("findings", i, "count", e.target.value)}
                            placeholder="qty"
                            className={inputCls + " w-20"}
                          />
                          <button
                            onClick={() => removeListItem("findings", i)}
                            className="text-white/40 hover:text-red-400 p-1"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <input
                          type="text"
                          value={f.notes || ""}
                          onChange={(e) => updateListItem("findings", i, "notes", e.target.value)}
                          placeholder="Notes"
                          className={inputCls}
                        />
                      </div>
                    ))}
                  </div>
                )}
                <button
                  onClick={() => addListItem("findings", { name: "", count: "", notes: "" })}
                  data-testid="mat-add-finding"
                  className="inline-flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-full border border-white/15 text-white/70 hover:border-[#D4AF37]/45 hover:text-[#D4AF37] transition"
                >
                  <Plus className="w-3 h-3" /> Add finding
                </button>
              </Section>

              {/* Price range */}
              <Section icon={Coins} title="Estimated price (INR)">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Low</label>
                    <input
                      type="number"
                      min="0"
                      step="100"
                      data-testid="mat-price-low"
                      value={materials.price_range.low}
                      onChange={(e) => updateField("price_range", "low", e.target.value)}
                      placeholder="e.g. 85000"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>High</label>
                    <input
                      type="number"
                      min="0"
                      step="100"
                      data-testid="mat-price-high"
                      value={materials.price_range.high}
                      onChange={(e) => updateField("price_range", "high", e.target.value)}
                      placeholder="e.g. 145000"
                      className={inputCls}
                    />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Price notes</label>
                  <input
                    type="text"
                    value={materials.price_range.notes}
                    onChange={(e) => updateField("price_range", "notes", e.target.value)}
                    placeholder="e.g. assuming 22kt @ ₹7,800/g + uncut polki @ ₹30k/ct"
                    className={inputCls}
                  />
                </div>
                {priceLine && (
                  <div
                    data-testid="mat-price-line"
                    className="mt-1 text-sm text-[#D4AF37] font-display tracking-wide"
                  >
                    Range · {priceLine}
                  </div>
                )}
              </Section>

              {/* Summary */}
              <Section icon={Sparkles} title="Manufacturing summary">
                <textarea
                  rows={3}
                  value={materials.summary}
                  data-testid="mat-summary"
                  onChange={(e) => update({ summary: e.target.value })}
                  placeholder="One-line handover note for the karigar / CAD team…"
                  className={inputCls + " resize-none"}
                />
              </Section>
            </>
          )}
        </div>

        <div className="border-t border-white/10 bg-[#0A0A0A] px-6 py-4 flex items-center gap-2">
          <button
            onClick={estimate}
            disabled={estimating || loading}
            data-testid="mat-estimate-btn"
            className="inline-flex items-center gap-2 text-xs px-4 py-2.5 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 text-[#D4AF37] hover:bg-[#D4AF37]/20 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {estimating ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Estimating…
              </>
            ) : hasAny ? (
              <>
                <RefreshCw className="w-3.5 h-3.5" /> Re-estimate
              </>
            ) : (
              <>
                <Sparkles className="w-3.5 h-3.5" /> Estimate with AI
              </>
            )}
          </button>
          <div className="flex-1" />
          <button
            onClick={() => onOpenChange(false)}
            className="text-xs px-4 py-2.5 rounded-full border border-white/15 text-white/75 hover:bg-white/5 transition"
          >
            Close
          </button>
          <button
            onClick={save}
            disabled={saving || loading || !dirty}
            data-testid="mat-save-btn"
            className="inline-flex items-center gap-2 text-xs px-5 py-2.5 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#F3CE5A] transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…
              </>
            ) : (
              <>
                <Save className="w-3.5 h-3.5" /> Save
              </>
            )}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
